// Trace a transparent PNG's alpha mask into a vector path so it can be used as a
// custom frame (Canva "Convert to Frame" for PNGs). The pure tracer below works
// on RGBA pixel data; the browser helper loads an image into a canvas first.
//
// Approach: per-row silhouette. For each sampled row we take the leftmost and
// rightmost opaque pixel, then walk the left edge down and the right edge back
// up to form a closed polygon. This is exact for horizontally-convex silhouettes
// (most logos/blobs/icons) and a clean approximation otherwise.

// data: RGBA Uint8ClampedArray (length width*height*4). Returns { path, vbW, vbH }
// in source-pixel coordinates, or null if nothing is opaque.
export function traceAlphaToPath(data, width, height, opts = {}) {
  const threshold = opts.threshold ?? 128;
  const step = Math.max(1, opts.step ?? Math.max(1, Math.round(Math.max(width, height) / 120)));
  const alphaAt = (x, y) => data[(y * width + x) * 4 + 3];

  const left = [];
  const right = [];
  const rowYs = [];
  for (let y = 0; y < height; y += step) {
    let minX = -1;
    let maxX = -1;
    for (let x = 0; x < width; x += step) {
      if (alphaAt(x, y) >= threshold) {
        if (minX === -1) minX = x;
        maxX = x;
      }
    }
    if (minX !== -1) {
      rowYs.push(y);
      left.push(minX);
      right.push(maxX + step - 1 < width ? maxX + step - 1 : width - 1);
    }
  }
  if (rowYs.length === 0) return null;

  const pts = [];
  for (let i = 0; i < rowYs.length; i++) pts.push([left[i], rowYs[i]]);          // left edge, top→bottom
  for (let i = rowYs.length - 1; i >= 0; i--) pts.push([right[i], rowYs[i]]);    // right edge, bottom→top

  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ') + ' Z';
  return { path: d, vbW: width, vbH: height };
}

// Browser-only: load an image src (PNG data URL or URL) → canvas → trace.
// Returns a Promise<{ path, vbW, vbH } | null>.
export function tracePngSrc(src, opts = {}) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const maxDim = opts.maxDim || 512;
        const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
        const w = Math.max(1, Math.round(img.naturalWidth * scale));
        const h = Math.max(1, Math.round(img.naturalHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);
        resolve(traceAlphaToPath(data, w, h, opts));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}
